import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { surveyAPI } from '../../utils/api';
import Alert from '../common/Alert';

const CATEGORIES = [
  'Coaching',
  'Empowerment',
  'Care',
  'Results',
  'Communication',
  'Career',
  'Vision',
  'TechnicalExpertise'
];

const SurveyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [existingEmails, setExistingEmails] = useState([]);
  const [newEmails, setNewEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    published: false,
    manager_id: '',
    questions: []
  });

  useEffect(() => {
    loadManagers();
    if (id) {
      loadSurvey();
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      let duplicateData = location.state?.duplicateData;
      let duplicateToken = location.state?.duplicateToken;
      
      if (!duplicateData) {
        const storedTokenKey = sessionStorage.getItem('latestSurveyDuplicateToken');
        if (storedTokenKey) {
          const storedData = sessionStorage.getItem(storedTokenKey);
          if (storedData) {
            try {
              duplicateData = JSON.parse(storedData);
              duplicateToken = storedTokenKey;
            } catch (err) {
              console.error('Failed to parse duplicate data from sessionStorage:', err);
            }
          }
        }
      }
      
      if (duplicateData) {
        setFormData({
          title: duplicateData.title,
          description: duplicateData.description,
          published: false,
          manager_id: '',
          questions: duplicateData.questions || []
        });
        setIsDuplicate(true);
        
        if (duplicateToken) {
          sessionStorage.removeItem(duplicateToken);
          sessionStorage.removeItem('latestSurveyDuplicateToken');
        }
      }
    }
  }, [id, location]);

  const loadManagers = async () => {
    try {
      const response = await fetch('/api/managers?unpaginated=true', {
        credentials: 'same-origin'
      });
      if (response.ok) {
        const data = await response.json();
        const activeManagers = data.filter(m => m.status === 'active');
        setManagers(activeManagers);
      }
    } catch (err) {
      console.error('Failed to load managers:', err);
    }
  };

  const loadSurvey = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getOne(id);
      setIsPublished(response.data.published);
      setFormData({
        title: response.data.title,
        description: response.data.description,
        published: response.data.published,
        manager_id: response.data.manager?.id || '',
        questions: response.data.questions || []
      });
      
      const emailsResponse = await fetch(`/api/surveys/${id}/invites`, {
        credentials: 'same-origin'
      });
      if (emailsResponse.ok) {
        const emailsData = await emailsResponse.json();
        setExistingEmails(emailsData.invites || []);
      }
    } catch (err) {
      setAlert({ show: true, message: 'Failed to load survey', type: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isExpired = (invite) => {
    if (!invite.expires_at) return false; // No expiration = never expires
    return new Date(invite.expires_at) < new Date();
  };

  const processEmails = (rawInput) => {
    if (!rawInput || !rawInput.trim()) return;
    
    // Split by comma, semicolon, newline, or multiple spaces
    const emailsToAdd = rawInput
      .split(/[,;\n\s]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e !== '');
    
    if (emailsToAdd.length === 0) return;
    
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const activeInvites = existingEmails.filter(e => !isExpired(e));
    
    const validEmails = [];
    const invalidEmails = [];
    const duplicateEmails = [];
    const seenInThisBatch = new Set();
    
    for (const email of emailsToAdd) {
      // Check if valid email format
      if (!emailPattern.test(email)) {
        invalidEmails.push(email);
        continue;
      }
      
      // Check if duplicate within this batch
      if (seenInThisBatch.has(email)) {
        duplicateEmails.push(email);
        continue;
      }
      
      // Check against existing invites (case-insensitive)
      if (activeInvites.some(e => e.email.toLowerCase() === email)) {
        duplicateEmails.push(email);
        continue;
      }
      
      // Check against already added new emails (case-insensitive)
      if (newEmails.some(e => e.toLowerCase() === email)) {
        duplicateEmails.push(email);
        continue;
      }
      
      seenInThisBatch.add(email);
      validEmails.push(email);
    }
    
    // Add valid emails
    if (validEmails.length > 0) {
      setNewEmails(prev => [...prev, ...validEmails]);
    }
    
    // Clear input
    setEmailInput('');
    
    // Show feedback
    if (invalidEmails.length > 0 && duplicateEmails.length > 0) {
      setAlert({ 
        show: true, 
        message: `Added ${validEmails.length} email(s). Skipped ${invalidEmails.length} invalid and ${duplicateEmails.length} duplicate email(s).`, 
        type: validEmails.length > 0 ? 'warning' : 'error' 
      });
    } else if (invalidEmails.length > 0) {
      setAlert({ 
        show: true, 
        message: `Added ${validEmails.length} email(s). Skipped ${invalidEmails.length} invalid email(s): ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`, 
        type: validEmails.length > 0 ? 'warning' : 'error' 
      });
    } else if (duplicateEmails.length > 0) {
      setAlert({ 
        show: true, 
        message: `Added ${validEmails.length} email(s). Skipped ${duplicateEmails.length} duplicate(s): ${duplicateEmails.slice(0, 3).join(', ')}${duplicateEmails.length > 3 ? '...' : ''}`, 
        type: validEmails.length > 0 ? 'warning' : 'error' 
      });
    } else if (validEmails.length > 1) {
      setAlert({ show: true, message: `Added ${validEmails.length} email(s) successfully!`, type: 'success' });
    }
  };
  
  const addEmailTag = () => {
    processEmails(emailInput);
  };
  
  const removeNewEmail = (email) => {
    setNewEmails(newEmails.filter(e => e !== email));
  };

  const removeExistingEmail = async (inviteId, email) => {
    if (!window.confirm(`Remove invitation for ${email}? This will expire their survey link.`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/surveys/${id}/invites/${inviteId}`, {
        method: 'DELETE',
        credentials: 'same-origin'
      });
      
      if (response.ok) {
        setExistingEmails(prev => prev.filter(e => e.id !== inviteId));
        setAlert({ show: true, message: 'Email invitation removed successfully', type: 'success' });
      } else {
        const data = await response.json();
        setAlert({ show: true, message: data.error || 'Failed to remove email invitation', type: 'error' });
      }
    } catch (err) {
      console.error('Failed to remove email:', err);
      setAlert({ show: true, message: 'Failed to remove email invitation', type: 'error' });
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, { text: '', category: 'Coaching' }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index][field] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeQuestion = (index) => {
    const question = formData.questions[index];
    if (question.id) {
      // Mark existing question for deletion
      const newQuestions = [...formData.questions];
      newQuestions[index] = { ...question, _destroy: true };
      setFormData({ ...formData, questions: newQuestions });
    } else {
      // Remove new unsaved question
      setFormData({
        ...formData,
        questions: formData.questions.filter((_, i) => i !== index)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setAlert({ show: true, message: 'Please enter a survey title', type: 'warning' });
      return;
    }
    
    if (!formData.manager_id) {
      setAlert({ show: true, message: 'Please select a manager', type: 'warning' });
      return;
    }
    
    if (formData.questions.length === 0) {
      setAlert({ show: true, message: 'Please add at least one question', type: 'warning' });
      return;
    }

    try {
      setLoading(true);
      
      // Capture any pending email in the input field before submission
      const pendingEmail = emailInput.trim();
      let allEmails = [...newEmails];
      let pendingEmailAdded = false;
      
      if (pendingEmail) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(pendingEmail)) {
          setAlert({ show: true, message: `Invalid email in input field: "${pendingEmail}". Please correct it or clear the field before submitting.`, type: 'warning' });
          setLoading(false);
          return;
        }
        
        // Check if email is not already in the list (case-insensitive)
        const activeInvites = existingEmails.filter(e => !isExpired(e));
        const isDuplicate = activeInvites.some(e => e.email.toLowerCase() === pendingEmail.toLowerCase()) || 
                          allEmails.some(e => e.toLowerCase() === pendingEmail.toLowerCase());
        
        if (isDuplicate) {
          setAlert({ show: true, message: `Email "${pendingEmail}" is already in the invitation list. Please remove it from the input field or press Enter to add it first.`, type: 'warning' });
          setLoading(false);
          return;
        }
        
        allEmails.push(pendingEmail);
        pendingEmailAdded = true;
      }
      
      const payload = {
        survey: {
          title: formData.title,
          description: formData.description,
          published: formData.published,
          manager_id: formData.manager_id,
          questions_attributes: formData.questions.map((q, index) => ({
            ...(q.id && { id: q.id }),
            text: q.text,
            category: q.category,
            position: index,
            ...(q._destroy && { _destroy: true })
          }))
        }
      };

      let surveyId = id;
      let surveyPublished = false;
      let pendingEmailsSent = 0;
      if (id) {
        const response = await surveyAPI.update(id, payload);
        surveyPublished = response.data.published;
        pendingEmailsSent = response.data.pending_emails_sent || 0;
      } else {
        const response = await surveyAPI.create(payload);
        surveyId = response.data.id;
        surveyPublished = response.data.published;
      }
      
      // Send invitations if new emails are provided
      if (allEmails.length > 0 && surveyId) {
        try {
          const emailListStr = allEmails.join('\n');
          const inviteResponse = await fetch(`/api/surveys/${surveyId}/send_invites`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ email_list: emailListStr })
          });
          
          if (inviteResponse.ok) {
            const inviteData = await inviteResponse.json();
            // Clear the email state after successful submission
            setNewEmails([]);
            setEmailInput('');
            
            const skippedMsg = inviteData.duplicates_skipped > 0 
              ? ` (${inviteData.duplicates_skipped} duplicate(s) skipped)` 
              : '';
            
            if (surveyPublished) {
              setAlert({ show: true, message: `Survey saved successfully! ${inviteData.invites_sent || 0} invitation email(s) sent${skippedMsg}.`, type: 'success' });
            } else {
              setAlert({ show: true, message: `Survey saved successfully! ${inviteData.invites_created || 0} invitation(s) created (emails will be sent when you publish the survey)${skippedMsg}.`, type: 'success' });
            }
          } else {
            setAlert({ show: true, message: 'Survey saved, but failed to create invitations.', type: 'warning' });
          }
        } catch (inviteErr) {
          console.error('Failed to send invitations:', inviteErr);
          setAlert({ show: true, message: 'Survey saved, but failed to create invitations.', type: 'warning' });
        }
      } else {
        if (pendingEmailsSent > 0) {
          setAlert({ show: true, message: `Survey published successfully! ${pendingEmailsSent} invitation email(s) sent to previously added recipients.`, type: 'success' });
        } else {
          setAlert({ show: true, message: 'Survey saved successfully!', type: 'success' });
        }
      }
      
      setTimeout(() => navigate('/admin/surveys'), 2000);
    } catch (err) {
      setAlert({ show: true, message: 'Failed to save survey: ' + (err.response?.data?.errors?.join(', ') || err.message), type: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      maxWidth: '900px'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: 'bold',
      color: '#333'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      fontSize: '1rem'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      fontSize: '1rem',
      minHeight: '100px',
      resize: 'vertical'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      fontSize: '1rem'
    },
    questionsSection: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px'
    },
    questionCard: {
      backgroundColor: 'white',
      padding: '1rem',
      marginBottom: '1rem',
      borderRadius: '4px',
      border: '1px solid #dee2e6'
    },
    questionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem'
    },
    button: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: 'bold'
    },
    primaryBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    secondaryBtn: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    dangerBtn: {
      backgroundColor: '#6c757d',
      color: 'white',
      padding: '0.5rem 1rem',
      fontSize: '0.875rem'
    },
    addBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    checkbox: {
      marginRight: '0.5rem'
    }
  };

  if (loading && id) return <div style={styles.container}>Loading...</div>;

  return (
    <div style={styles.container}>
      <Alert 
        show={alert.show}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      
      <h2>{id ? 'Edit Survey' : 'Create New Survey'}</h2>
      
      {isDuplicate && (
        <div style={{
          backgroundColor: '#d1ecf1',
          color: '#0c5460',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          border: '1px solid #bee5eb'
        }}>
          ℹ️ <strong>Duplicating Survey:</strong> This form has been pre-filled with data from an existing survey. 
          Please select a manager and review all fields before saving.
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Survey Title *</label>
          <input
            type="text"
            style={styles.input}
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., Manager Effectiveness Survey Q1 2025"
            required
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description</label>
          <textarea
            style={styles.textarea}
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Describe the purpose of this survey..."
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Manager *</label>
          <select
            style={styles.select}
            value={formData.manager_id}
            onChange={(e) => setFormData({...formData, manager_id: e.target.value})}
            required
            disabled={isPublished}
          >
            <option value="">Select a manager...</option>
            {managers.map(manager => (
              <option key={manager.id} value={manager.id}>
                {manager.full_name} - {manager.department}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={{...styles.label, display: 'flex', alignItems: 'center'}}>
            <input
              type="checkbox"
              style={styles.checkbox}
              checked={formData.published}
              onChange={(e) => setFormData({...formData, published: e.target.checked})}
            />
            Publish immediately (users can submit responses)
          </label>
        </div>

        <div style={styles.questionsSection}>
          <h3>Questions</h3>
          {isPublished && (
            <div style={{backgroundColor: '#fff3cd', color: '#856404', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #ffeeba'}}>
              <strong>⚠️ Questions Locked:</strong> This survey is published. Question editing is disabled to preserve response integrity. You can still edit the title, description, and manager.
            </div>
          )}

          {formData.questions.filter(q => !q._destroy).map((question, index) => (
            <div key={index} style={styles.questionCard}>
              <div style={styles.questionHeader}>
                <strong>Question {index + 1}</strong>
                <button
                  type="button"
                  onClick={() => removeQuestion(index)}
                  style={{...styles.button, ...styles.dangerBtn, ...(isPublished ? {opacity: 0.5, cursor: 'not-allowed'} : {})}}
                  disabled={isPublished}
                  title={isPublished ? 'Cannot remove questions from published surveys' : 'Remove this question'}
                >
                  Remove
                </button>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <select
                  style={{...styles.select, ...(isPublished ? {backgroundColor: '#e9ecef', cursor: 'not-allowed'} : {})}}
                  value={question.category}
                  onChange={(e) => updateQuestion(index, 'category', e.target.value)}
                  disabled={isPublished}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Question Text *</label>
                <textarea
                  style={{...styles.textarea, ...(isPublished ? {backgroundColor: '#e9ecef', cursor: 'not-allowed'} : {})}}
                  value={question.text}
                  onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                  placeholder="Enter your question..."
                  required
                  disabled={isPublished}
                />
              </div>
            </div>
          ))}

          {formData.questions.length === 0 && (
            <p style={{textAlign: 'center', color: '#6c757d', padding: '2rem'}}>
              No questions added yet. Click "Add Question" to start.
            </p>
          )}

          <div style={styles.questionHeader}>
            <button
              type="button"
              onClick={addQuestion}
              style={{...styles.button, ...styles.addBtn, ...(isPublished ? {opacity: 0.5, cursor: 'not-allowed'} : {})}}
              disabled={isPublished}
              title={isPublished ? 'Cannot add questions to published surveys' : 'Add a new question'}
            >
              Add Question
            </button>
          </div>
        </div>

        <div style={{...styles.formGroup, marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f0f8ff', borderRadius: '8px'}}>
          <label style={styles.label}>
            📧 Email Invitations
          </label>
          
          <div style={{
            minHeight: '100px',
            padding: '0.75rem',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            backgroundColor: 'white',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'flex-start',
            alignContent: 'flex-start'
          }}>
            {existingEmails.filter(invite => !isExpired(invite)).map(invite => {
              // On Edit page (id exists), use rgb(7, 99, 142) for all tags
              const color = id ? 'rgb(7, 99, 142)' : (invite.used ? '#28a745' : '#ffc107');
              
              return (
                <div key={`existing-${invite.id}`} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'white',
                  border: `2px solid ${color}`,
                  borderRadius: '20px',
                  fontSize: '0.875rem'
                }}>
                  <span style={{color: color, fontWeight: '500'}}>
                    {invite.email}
                  </span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: color,
                    fontWeight: '600'
                  }}>
                    {invite.used ? '✓' : '⏳'}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExistingEmail(invite.id, invite.email)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: color,
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      padding: 0,
                      lineHeight: 1,
                      marginLeft: '0.25rem'
                    }}
                    title="Remove invitation"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            
            {newEmails.map(email => {
              // On Edit page (id exists), use rgb(7, 99, 142) for new tags too
              const color = id ? 'rgb(7, 99, 142)' : '#6c757d';
              
              return (
                <div key={`new-${email}`} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'white',
                  border: `2px solid ${color}`,
                  borderRadius: '20px',
                  fontSize: '0.875rem'
                }}>
                  <span style={{color: color, fontWeight: '500'}}>
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeNewEmail(email)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: color,
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      padding: 0,
                      lineHeight: 1,
                      marginLeft: '0.25rem'
                    }}
                    title="Remove email"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            
            <input
              type="text"
              style={{
                flex: '1 1 200px',
                minWidth: '200px',
                padding: '0.5rem',
                border: 'none',
                outline: 'none',
                fontSize: '0.875rem'
              }}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEmailTag();
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                if (pastedText) {
                  processEmails(pastedText);
                }
              }}
              placeholder="Type or paste emails (comma, semicolon, or space separated)..."
            />
          </div>
          
          <p style={{fontSize: '0.75rem', color: '#6c757d', marginTop: '0.5rem'}}>
            Type or paste multiple email addresses separated by commas, semicolons, or spaces. Press Enter to add. Click × to remove. Duplicates are automatically skipped.
          </p>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="submit"
            style={{...styles.button, ...styles.primaryBtn}}
            disabled={loading}
          >
            {loading ? 'Saving...' : (id ? 'Update Survey' : 'Create Survey')}
          </button>
          <button
            type="button"
            onClick={() => {
              const duplicateToken = location.state?.duplicateToken || sessionStorage.getItem('latestSurveyDuplicateToken');
              if (duplicateToken) {
                sessionStorage.removeItem(duplicateToken);
                sessionStorage.removeItem('latestSurveyDuplicateToken');
              }
              navigate('/admin/surveys');
            }}
            style={{...styles.button, ...styles.secondaryBtn}}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default SurveyForm;
